import { useCallback, useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useYouTubeAccountReset } from '@/lib/youtubeConnection';
import { YouTubeDisconnectButton } from '@/components/channel/YouTubeDisconnectButton';
import { formatApiError } from '@/lib/formatApiError';
import { formatCount } from '@/lib/channel';
import { SeoScoreRing } from '@/components/channel/SeoScoreRing';
import { VideoThumb } from '@/components/channel/VideoThumb';

function categoryAside(genre) {
  const label = String(genre?.label || '').toLowerCase();
  const category = String(genre?.category_label || '').trim();
  const folded = category.toLowerCase();
  if (!folded || folded === label || label.includes(folded) || folded.includes(label)) return '';
  return category;
}

function VideoRow({ video, rank }) {
  const highlights = (video.seo_highlights || []).slice(0, 3);
  return (
    <a
      href={video.url}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-4 rounded-[16px] bg-white/[0.04] px-3.5 py-3.5 hover:bg-white/[0.07]"
    >
      {rank ? (
        <span className="w-6 shrink-0 text-center text-sm font-semibold text-[#7d8799]">{rank}</span>
      ) : null}
      <VideoThumb video={video} className="h-16 w-28 shrink-0 rounded-xl" />
      <span className="min-w-0 flex-1 space-y-1.5">
        <span className="block text-sm font-semibold leading-5 text-white line-clamp-2">{video.title}</span>
        <span className="block text-xs text-[#8b95a7] truncate">
          {video.channel_title} · {formatCount(video.views)} views
        </span>
        {highlights.length > 0 ? (
          <span className="flex flex-wrap gap-1.5 pt-0.5">
            {highlights.map((item) => (
              <span key={item} className="rounded-full bg-black/30 px-2 py-0.5 text-[10px] leading-4 text-[#9aa6b8]">
                {item}
              </span>
            ))}
          </span>
        ) : null}
      </span>
      <span className="shrink-0 pl-1">
        <SeoScoreRing score={video.seo_score} size={64} />
      </span>
    </a>
  );
}

export function ChannelTrendsPage() {
  const [region, setRegion] = useState('IN');
  const [data, setData] = useState(null);
  const [peers, setPeers] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: payload }, peerResult] = await Promise.all([
        api.get('/channel/trends', { params: { region } }),
        api.get('/channel/peers').catch(() => ({ data: null })),
      ]);
      setData(payload);
      setPeers(peerResult?.data || null);
    } catch (err) {
      toast.error(formatApiError(err, 'Could not load YouTube trends'));
    } finally {
      setLoading(false);
    }
  }, [region]);

  const clearChannel = useCallback(() => {
    setPeers(null);
    setData({ connected: false, top: [], by_genre: [], regions: [] });
  }, []);
  useYouTubeAccountReset(clearChannel);

  useEffect(() => {
    load();
  }, [load]);

  const regions = data?.regions?.length
    ? data.regions
    : [
        { id: 'IN', label: 'India' },
        { id: 'US', label: 'United States' },
        { id: 'GB', label: 'United Kingdom' },
      ];
  const genres = data?.by_genre || [];
  const top = data?.top || [];

  return (
    <main className="w-full px-3 sm:px-8 py-6 sm:py-8 space-y-10 font-jakarta overflow-x-hidden">
      <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0 max-w-2xl space-y-2">
          <h1 className="text-2xl font-semibold tracking-tight text-white">General Trends</h1>
          <p className="text-sm leading-6 text-[#8b95a7]">
            YouTube’s most-popular videos in the genres of stories you have made, and the top 10 in this country.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 lg:justify-end lg:max-w-md lg:pt-1">
          <YouTubeDisconnectButton />
          {regions.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setRegion(item.id)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${
                region === item.id
                  ? 'bg-[#13322d] text-[#34d399] border-[#214f48]'
                  : 'text-[#7d8799] border-[#1f2635] hover:text-white'
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="h-24 flex items-center gap-2 text-xs text-[#7d8799]">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading trending videos…
        </div>
      ) : null}

      {data?.connected && (peers?.channels?.length || peers?.videos?.length) ? (
        <section className="space-y-4">
          <h2 className="text-base font-semibold text-white">Channels like yours</h2>
          <div className="grid xl:grid-cols-2 gap-3">
            {(peers.channels || []).map((channel) => (
              <p key={channel.channel_id} className="rounded-[16px] bg-white/[0.04] px-3 py-3 text-sm text-white">
                {channel.title}
                <span className="block text-xs text-[#8b95a7] mt-1">{formatCount(channel.subscriber_count)} subscribers</span>
              </p>
            ))}
          </div>
          <h2 className="text-base font-semibold text-white">Videos like yours</h2>
          <div className="grid xl:grid-cols-2 gap-4">
            {(peers.videos || []).map((video) => (
              <a
                key={video.video_id}
                href={video.url}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-[16px] bg-white/[0.04] px-3 py-3 hover:bg-white/[0.07]"
              >
                <span className="block text-sm font-semibold text-white line-clamp-2">{video.title}</span>
                <span className="block text-xs text-[#8b95a7] mt-1">
                  {video.channel_title} · {formatCount(video.view_count)} views
                </span>
              </a>
            ))}
          </div>
          {(peers.updates || []).length > 0 ? (
            <ul className="text-sm text-[#c5cedb] space-y-1">
              {peers.updates.map((line) => <li key={line}>{line}</li>)}
            </ul>
          ) : null}
          {(peers.phrases || []).length > 0 ? (
            <p className="text-sm text-[#8b95a7]">Try these words: {peers.phrases.join(', ')}</p>
          ) : null}
          {peers.strategy ? <p className="text-sm text-white">{peers.strategy}</p> : null}
        </section>
      ) : null}

      {data && !data.connected ? (
        <p className="text-sm text-[#d5dbe6]">Connect YouTube on the dashboard to load trending videos.</p>
      ) : null}

      {genres.filter((genre) => (genre.videos || []).length > 0).map((genre) => (
        <section key={genre.category_id} className="space-y-4">
          <div className="space-y-1">
            <h2 className="text-base font-semibold text-white">Trending in {genre.label}</h2>
            {categoryAside(genre) ? (
              <p className="text-xs text-[#8b95a7]">{categoryAside(genre)}</p>
            ) : null}
          </div>
          <div className="grid xl:grid-cols-2 gap-4">
            {genre.videos.map((video) => (
              <VideoRow key={`${genre.category_id}-${video.video_id}`} video={video} />
            ))}
          </div>
        </section>
      ))}

      {!loading && data?.connected && genres.length === 0 ? (
        <p className="text-sm text-[#9aa6b8]">
          Make a story and Twinnee will match YouTube trends to its genre. The top 10 are below.
        </p>
      ) : null}

      <section className="space-y-4">
        <h2 className="text-base font-semibold text-white">Top 10 trending on YouTube</h2>
        {top.length ? (
          <div className="space-y-3">
            {top.map((video, index) => (
              <VideoRow key={video.video_id} video={video} rank={index + 1} />
            ))}
          </div>
        ) : !loading && data?.connected ? (
          <p className="text-xs text-[#7d8799]">The trending chart is empty for this country right now.</p>
        ) : null}
      </section>
    </main>
  );
}
